/* eslint-disable no-console */
import { Component, OnInit } from '@angular/core';
import { UserServiceService } from './services/user-service.service';
import { GobiernoUser, Rol, User } from './interfaces/user.interface';
import { filter } from '../utils/filter'; // Importar la función de filtro
import { firstValueFrom } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ViewDireccionModalComponent } from './modals/view-direccion-modal.component';
import { ViewEmpresaModalComponent } from './modals/view-empresa-modal.component';
import { ViewRepresentanteModalComponent } from './modals/view-representante-modal.component';
import { FiltrosUsuarios } from './interfaces/usuarios-filtros.interface';
import { EditRolModalComponent } from './modals/edit-rol-modal/edit-rol-modal.component';
import { InactivarUsuarioModalComponent } from './modals/inactivar-usuario-modal/inactivar-usuario-modal.component';
import { VerUsuarioModalComponent } from './modals/ver-usuario-modal/ver-usuario-modal.component';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-busqueda-usuario',
    templateUrl: './busqueda-usuario.component.html',
    styleUrls: ['./busqueda-usuario.component.css'],
})
export class BusquedaUsuarioComponent implements OnInit {
    public constructor(
        private userService: UserServiceService,
        private modalService: NgbModal // Inyecta NgbModal
    ) {}

    public usuarios: User[];
    public searchTerm = '';
    public filteredUsers: User[];
    public usuarioCompanys;
    public roles: Rol[] = [];
    public activeTab = 'representantes';
    public gobiernoUsers: GobiernoUser[] = [];
    public filteredGobiernoUsers: GobiernoUser[] = [];
    public filtros: FiltrosUsuarios = {
        nombre: '',
        correo: '',
        cargo: '',
        rol: '',
        entidad: '',
        estado: '',
        username: '',
    };
    public tiposEstado = [
        { id: 'Activo', nombre: 'Activo' },
        { id: 'Inactivo', nombre: 'Inactivo' },
    ];
    public ngOnInit(): void {
        this.mostrarUsuarios();
        this.consultarRoles();
    }

    public async mostrarUsuarios(): Promise<void> {
        const usuariosCompany = await this.userService.getUserCompanys();
        this.usuarioCompanys = usuariosCompany;
        const data = await this.userService.GetUsers(
            'representante-agente-vigilado'
        );
        this.usuarios = data;
        this.filterUsers();
    }

    public async cambiarTab(tab: string): Promise<void> {
        this.activeTab = tab;
        this.searchTerm = '';

        if (tab === 'gobierno') {
            await this.cargarUsuariosGobierno();
        } else if (tab === 'representantes') {
            this.mostrarUsuarios();
        }
    }

    public async cargarUsuariosGobierno(): Promise<void> {
        this.filteredGobiernoUsers = [];
        this.filtros = {
            nombre: '',
            correo: '',
            cargo: '',
            rol: '',
            entidad: '',
            estado: '',
            username: '',
        };
        try {
            const response = await this.userService.GetGobiernoUsers(
                'usuario/list?tab=Gobierno'
            );
            this.gobiernoUsers = response;
            this.aplicarFiltros();
        } catch (error) {
            console.error('Error cargando usuarios de gobierno:', error);
        }
    }

    public onSearchInput(): void {
        if (this.activeTab === 'representantes') {
            this.filterUsers();
        } else {
            this.filterGobiernoUsers();
        }
    }

    public filterUsers(): void {
        this.filteredUsers = filter(this.searchTerm, this.usuarioCompanys);
    }

    public filterGobiernoUsers(): void {
        if (!this.searchTerm.trim()) {
            this.filteredGobiernoUsers = [...this.gobiernoUsers];
            return;
        }

        const term = this.searchTerm.toLowerCase();
        this.filteredGobiernoUsers = this.gobiernoUsers.filter((user) => {
            return (
                user.nombre?.toLowerCase().includes(term) ||
                user.apellido?.toLowerCase().includes(term) ||
                user.correo?.toLowerCase().includes(term) ||
                user.cargo?.toLowerCase().includes(term) ||
                user.roles?.some((rol) =>
                    rol.nombre?.toLowerCase().includes(term)
                ) ||
                user.entidad?.toLowerCase().includes(term) ||
                user.username?.toLowerCase().includes(term)
            );
        });
    }

    public obtenerNombresRoles(roles: Rol[]): string {
        if (!roles || roles.length === 0) return '';
        return roles.map((rol) => rol.nombre).join(', ');
    }

    /**
     * Filtra los usuarios según el valor de la columna seleccionada.
     *
     * @param columna - El nombre de la columna por la cual se filtra (por ejemplo, 'Nombre Completo', 'Correo electrónico ', 'Cargo', 'Rol', etc).
     * @param valor - El valor que se va a filtrar en la columna seleccionada.
     */
    public aplicarFiltros(): void {
        let usuariosFiltrados = [...this.gobiernoUsers];

        // Filtrar por nombre
        if (this.filtros.nombre) {
            const term = this.filtros.nombre.toLowerCase();
            usuariosFiltrados = usuariosFiltrados.filter((user) =>
                `${user.nombre} ${user.apellido}`.toLowerCase().includes(term)
            );
        }

        // Filtrar por correo
        if (this.filtros.correo) {
            const term = this.filtros.correo.toLowerCase();
            usuariosFiltrados = usuariosFiltrados.filter((user) =>
                user.correo?.toLowerCase().includes(term)
            );
        }

        // Filtrar por cargo
        if (this.filtros.cargo) {
            const term = this.filtros.cargo.toLowerCase();
            usuariosFiltrados = usuariosFiltrados.filter((user) =>
                user.cargo?.toLowerCase().includes(term)
            );
        }

        // Filtrar por rol
        if (this.filtros.rol) {
            const term = this.filtros.rol.toLowerCase();
            usuariosFiltrados = usuariosFiltrados.filter((user) =>
                user.roles?.some((rol) =>
                    rol.nombre?.toLowerCase().includes(term)
                )
            );
        }

        // Filtrar por entidad
        if (this.filtros.entidad) {
            const term = this.filtros.entidad.toLowerCase();
            usuariosFiltrados = usuariosFiltrados.filter((user) =>
                user.entidad?.toLowerCase().includes(term)
            );
        }

        // Filtrar por estado: solo se aplica si el valor es Activo o Inactivo,
        // cualquier otro valor se ignora para no vaciar la tabla
        const estadoFiltro = this.filtros.estado?.trim().toLowerCase();
        if (estadoFiltro === 'activo' || estadoFiltro === 'inactivo') {
            const estadoEsperado = estadoFiltro === 'activo';
            usuariosFiltrados = usuariosFiltrados.filter(
                (user) => (user.estado === true) === estadoEsperado
            );
        }

        // Filtrar por usuario
        if (this.filtros.username) {
            const term = this.filtros.username.toLowerCase();
            usuariosFiltrados = usuariosFiltrados.filter((user) =>
                user.username?.toLowerCase().includes(term)
            );
        }

        this.filteredGobiernoUsers = usuariosFiltrados;
    }

    public onFiltro(columna: string, valor: string): void {
        switch (columna) {
            case 'nombre':
                this.filtros.nombre = valor;
                break;
            case 'correo':
                this.filtros.correo = valor;
                break;
            case 'cargo':
                this.filtros.cargo = valor;
                break;
            case 'rol':
                this.filtros.rol = valor;
                break;
            case 'entidad':
                this.filtros.entidad = valor;
                break;
            case 'estado':
                this.filtros.estado = valor;
                break;
            case 'username':
                this.filtros.username = valor;
                break;
        }
        this.aplicarFiltros();
    }

    public getNombreCompleto(representante: User['representante']): string {
        if (!representante) {
            return '';
        }
        return `${representante.nombre || ''} ${representante.apellido || ''}`.trim();
    }

    public abrirModalVerDireccion(empresaId: number): void {
        if (!empresaId) {
            console.warn('ID de empresa no válido para ver dirección.');
            return;
        }

        const modalRef = this.modalService.open(ViewDireccionModalComponent, {
            size: 'lg',
            centered: true,
            scrollable: true,
        });

        modalRef.componentInstance.empresaId = empresaId;

        modalRef.result.then(
            (result) => {
                console.log(`Modal cerrado con resultado: ${result}`);
            },
            (reason) => {
                console.log(`Modal descartado por: ${reason}`);
            }
        );
    }

    public abrirModalVerEmpresa(
        empresaId: number,
        representante: User['representante']
    ): void {
        if (!empresaId) {
            console.warn('ID de empresa no válido para ver detalles.');
            return;
        }

        const modalRef = this.modalService.open(ViewEmpresaModalComponent, {
            size: 'lg',
            centered: true,
            scrollable: true,
        });

        modalRef.componentInstance.empresaId = empresaId;
        modalRef.componentInstance.nombreRepresentante =
            this.getNombreCompleto(representante);

        modalRef.result.then(
            (result) => {
                console.log(
                    `Modal de empresa cerrado con resultado: ${result}`
                );
            },
            (reason) => {
                console.log(`Modal de empresa descartado por: ${reason}`);
            }
        );
    }

    public abrirModalVerRepresentante(
        representante: User['representante']
    ): void {
        if (!representante) {
            console.warn(
                'Datos del representante no válidos para ver detalles.'
            );
            return;
        }

        const modalRef = this.modalService.open(
            ViewRepresentanteModalComponent,
            {
                size: 'lg',
                centered: true,
                scrollable: true,
            }
        );
        modalRef.componentInstance.representante = representante;
        modalRef.result.then(
            (result) => {
                console.log(
                    `Modal de representante cerrado con resultado: ${result}`
                );
            },
            (reason) => {
                console.log(`Modal de representante descartado por: ${reason}`);
            }
        );
    }

    public async actualizarEstado(
        usuarioID: number,
        estado: string
    ): Promise<void> {
        const nuevoEstado = estado === 'Activo' ? 'Desactivado' : 'Activo';
        const url = `representante-agente-vigilado/activar-o-desactivar-usuario?usuarioID=${usuarioID}&estado=${nuevoEstado}`;
        try {
            await this.userService.PutUser(url);
            await this.mostrarUsuarios();
        } catch (error) {
            console.error('Error al actualizar estado del usuario:', error);
        }
    }

    public abrirModalEditarRol(usuario: GobiernoUser): void {
        const modalRef = this.modalService.open(EditRolModalComponent, {
            size: 'lg',
            centered: true,
            scrollable: true,
        });
        modalRef.componentInstance.usuario = usuario;
        modalRef.componentInstance.roles = this.roles;

        modalRef.result
            .then(() => this.cargarUsuariosGobierno())
            .catch(() => undefined);
    }

    public consultarRoles(): void {
        try {
            this.userService.getRoles().subscribe((response) => {
                console.log('roles', response);
                this.roles = response;
            });
        } catch (error) {
            console.error('Error al consultar roles:', error);
        }
    }

    public async actualizarEstadoGobierno(
        usuarioID: number,
        estado: boolean
    ): Promise<void> {
        const usuario = this.gobiernoUsers.find((u) => u.id === usuarioID);
        const nombreUsuario = usuario
            ? `${usuario.nombre} ${usuario.apellido}`.trim()
            : 'el usuario';
        const accion = estado ? 'inactivado' : 'activado';
        const modalRef = this.modalService.open(
            InactivarUsuarioModalComponent,
            {
                size: 'lg',
                centered: true,
                scrollable: true,
            }
        );

        if (usuario) {
            modalRef.componentInstance.usuario = usuario;
        }

        try {
            const { motivo, otroMotivo } = await firstValueFrom(
                modalRef.closed
            );

            await this.userService.postActualizarEstadoUsuario({
                usuarioID,
                estado: !estado,
                motivoId: motivo,
                otroMotivo,
            });

            Swal.fire({
                icon: 'success',
                title: `Usuario ${accion}`,
                text: `Se ha ${accion} exitosamente el usuario "${nombreUsuario}".`,
            });

            await this.cargarUsuariosGobierno();
        } catch {
            Swal.fire({
                icon: 'info',
                title: `${estado ? 'Inactivación' : 'Activación'} cancelada`,
                text: `No se realizó la ${estado ? 'inactivación' : 'activación'} del usuario.`,
                showConfirmButton: true,
            });
        }
    }

    public verUsuarioGobierno(usuarioID: number): void {
        const usuario = this.gobiernoUsers.find(
            (item) => item.id === usuarioID
        );
        if (!usuario) {
            return;
        }

        const modalRef = this.modalService.open(VerUsuarioModalComponent, {
            size: 'lg',
            centered: true,
            scrollable: true,
        });
        modalRef.componentInstance.usuario = usuario;
    }
}
